"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const guaranteeItems = [{
  icon: Clock,
  label: "24/7 Rapid Response"
}, {
  icon: ShieldCheck,
  label: "Upfront Honest Pricing"
}, {
  icon: Wrench,
  label: "Licensed GA Specialists"
}];
export default function HeaterTrouble() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="water-heater-trouble" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-md md:p-12 lg:p-16">
          {/* Decorative Gold Accent Stripe */}
          <div className="absolute top-0 left-0 h-full w-2 bg-accent" />

          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Status Pill */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }} className="mb-4 inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              Emergency Water Heater Dispatch in Jonesboro
            </motion.div>

            {/* Headline */}
            <motion.h2 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.06,
            ease: "easeOut"
          }} className="font-default text-3xl font-bold tracking-tight text-card-foreground sm:text-4xl lg:text-5xl">
              Water Heater Trouble? We Are Ready Now.
            </motion.h2>

            {/* Subtitle / Lead Paragraph */}
            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            cold: true,
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.12,
            ease: "easeOut"
          }} className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
              No hot water, tank leaks, or strange rumbling sounds? Our local Jonesboro plumbers diagnose and resolve your water heater issues immediately with fast 24/7 service.
            </motion.p>

            {/* Action Buttons */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.18,
            ease: "easeOut"
          }} className="mt-8 flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row">
              <Button asChild size="lg" className="w-full rounded-full bg-accent px-8 py-6 text-base font-bold text-accent-foreground shadow-md transition-[background-color,box-shadow,transform] hover:bg-accent/90 sm:w-auto">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <Phone className="size-5 fill-current" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild size="lg" className="w-full rounded-full bg-primary px-8 py-6 text-base font-semibold text-primary-foreground shadow-sm transition-[background-color,box-shadow,transform] hover:bg-primary/90 sm:w-auto">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-5" />
                </Link>
              </Button>
            </motion.div>

            {/* Fast Guarantees List */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.24,
            ease: "easeOut"
          }} className="mt-10 flex flex-wrap items-center justify-center gap-6 border-t border-border pt-6 md:gap-10">
              {guaranteeItems.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.label} data-index={i} className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-primary">
                      <IconComponent className="size-4" />
                    </span>
                    <span>{item.label}</span>
                  </div>;
            })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}